<template>
  <div id="judge-assignment-list">
    <!-- Full-screen loading overlay -->
    <v-overlay :value="ProgressLoadingFlag" z-index="999">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="white"
        ></v-progress-circular>
        <div class="mt-3 white--text subtitle-1">Processing...</div>
      </div>
    </v-overlay>

    <!-- ── Gradient Header ───────────────────────────────────────────── -->
    <v-card elevation="12" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-5 pa-sm-8">
          <v-row align="center" justify="space-between">
            <v-col cols="12" md="9" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="38px" color="white">mdi-account-badge</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">{{ PageTitle }}</h1>
                <p class="header-subtitle white--text mb-0">
                  {{ PageDescription }}
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="3" class="d-flex justify-md-end mt-3 mt-md-0">
              <v-btn
                class="action-btn"
                icon
                @click="refreshPageData"
                title="Refresh"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <!-- ── Filter Card ───────────────────────────────────────────────── -->
    <v-card class="filter-card mb-5" elevation="4">
      <div class="filter-card-header">
        <v-icon color="white" class="mr-2" small>mdi-filter-outline</v-icon>
        <span class="filter-card-title">Search Filters</span>
      </div>
      <v-card-text class="pa-5">
        <v-form
          ref="form2"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="searchForm"
        >
          <v-row dense>
            <!-- Scholarship Status -->
            <v-col cols="12" sm="6" lg="4" md="6">
              <div class="field-label">
                <v-icon x-small color="#90A4AE" class="mr-1"
                  >mdi-list-status</v-icon
                >LO Status
                <!-- <span class="optional-tag">Optional</span> -->
              </div>
              <v-autocomplete
                v-model="Status"
                :items="StatusOptions"
                :rules="StatusOptionsRules"
                :loading="StatusOptionsLoading"
                item-text="text"
                item-value="value"
                placeholder="Select Status"
                prepend-inner-icon="mdi-check-circle-outline"
                dense
                clearable
                outlined
                hide-details="auto"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Member details card -->
          <v-row v-if="member && member.MembershipId" class="mt-3">
            <v-col cols="12" sm="10" md="7" lg="5">
              <v-card class="portal-details-card elevation-3">
                <div class="portal-card-header">
                  <v-icon color="white" class="mr-2">mdi-shield-account</v-icon>
                  <span class="portal-card-title">Portal Details</span>
                  <v-chip
                    x-small
                    color="#e53935"
                    text-color="white"
                    class="ml-2"
                    >Member</v-chip
                  >
                </div>
              </v-card>
            </v-col>
          </v-row>
          <v-row justify="center" class="mt-4">
            <v-col cols="12" sm="6" md="4" lg="3" class="d-flex justify-center">
              <v-btn class="search-btn" @click="searchForm" min-width="180">
                <v-icon left>mdi-magnify</v-icon> Search
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- ── No Records Found ─────────────────────────────────────────── -->
    <v-card
      v-if="SearchCompleted && rows.length === 0"
      class="no-records-card"
      elevation="4"
    >
      <v-card-text class="pa-8 text-center">
        <div class="no-records-db-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 200 180"
            class="no-records-svg"
          >
            <!-- Shadow -->
            <ellipse cx="100" cy="168" rx="60" ry="8" fill="#e0e7ef" />
            <!-- DB cylinder bottom -->
            <ellipse cx="100" cy="130" rx="52" ry="14" fill="#b0bec5" />
            <rect x="48" y="100" width="104" height="30" fill="#cfd8dc" />
            <ellipse cx="100" cy="100" rx="52" ry="14" fill="#eceff1" />
            <!-- DB cylinder middle -->
            <rect x="48" y="68" width="104" height="32" fill="#e3eaf2" />
            <ellipse cx="100" cy="68" rx="52" ry="14" fill="#f0f4f8" />
            <!-- DB cylinder top -->
            <rect x="48" y="38" width="104" height="30" fill="#e8f0fb" />
            <ellipse
              cx="100"
              cy="38"
              rx="52"
              ry="14"
              fill="#fff"
              stroke="#b0bec5"
              stroke-width="1.5"
            />
            <!-- Shine on top -->
            <ellipse
              cx="85"
              cy="35"
              rx="18"
              ry="5"
              fill="rgba(255,255,255,0.55)"
            />
            <!-- Magnifier -->
            <circle
              cx="148"
              cy="44"
              r="18"
              fill="white"
              stroke="#1a3a5c"
              stroke-width="3"
            />
            <line
              x1="161"
              y1="57"
              x2="174"
              y2="70"
              stroke="#1a3a5c"
              stroke-width="4"
              stroke-linecap="round"
            />
            <!-- X inside magnifier -->
            <line
              x1="141"
              y1="37"
              x2="155"
              y2="51"
              stroke="#e53935"
              stroke-width="2.5"
              stroke-linecap="round"
            />
            <line
              x1="155"
              y1="37"
              x2="141"
              y2="51"
              stroke="#e53935"
              stroke-width="2.5"
              stroke-linecap="round"
            />
            <!-- Lines on db (rows) -->
            <line
              x1="68"
              y1="112"
              x2="132"
              y2="112"
              stroke="#90a4ae"
              stroke-width="2"
              stroke-linecap="round"
            />
            <line
              x1="68"
              y1="120"
              x2="115"
              y2="120"
              stroke="#90a4ae"
              stroke-width="2"
              stroke-linecap="round"
            />
            <line
              x1="68"
              y1="80"
              x2="132"
              y2="80"
              stroke="#b0bec5"
              stroke-width="2"
              stroke-linecap="round"
            />
            <line
              x1="68"
              y1="88"
              x2="115"
              y2="88"
              stroke="#b0bec5"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <h3 class="no-records-title mt-4">No Records Found</h3>
        <p class="no-records-subtitle mt-2">
          No applications match your search criteria.<br />
          Try adjusting your filters and search again.
        </p>
      </v-card-text>
    </v-card>

    <!-- ── Results Card ──────────────────────────────────────────────── -->
    <v-card class="table-card" elevation="4" v-if="rows.length > 0">
      <v-card-text class="pa-4 pa-sm-5">
        <!-- Table toolbar -->
        <v-row align="center" class="mb-4" no-gutters>
          <v-col cols="12" sm="6" class="mb-3 mb-sm-0">
            <div class="d-flex align-center">
              <v-icon color="#1a3a5c" class="mr-2">mdi-table</v-icon>
              <span class="table-title">Results</span>
              <v-chip small color="#1a3a5c" text-color="white" class="ml-2">
                {{ rows.length }} Record{{ rows.length !== 1 ? "s" : "" }}
              </v-chip>
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            class="d-flex justify-sm-end align-center"
            style="gap: 8px"
          >
            <v-btn
              v-if="DownloadFlag"
              class="download-btn"
              small
              @click.prevent="
                generateExcel(rows, ExcelFields, ExcelFileName, 'My Worksheet')
              "
            >
              <v-icon left small>mdi-download</v-icon> Export
            </v-btn>
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search in table"
              single-line
              hide-details
              dense
              outlined
              class="search-field"
              style="max-width: 260px"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-data-table
          class="results-table"
          v-model="selected"
          :headers="tableColumns1"
          :items="rows"
          :items-per-page="50"
          :search="search"
          :footer-props="{ 'items-per-page-options': [10, 20, 30, 40, 50] }"
        >
          <template v-slot:[`item.LomStatusTxt`]="{ item }">
            <v-chip
              small
              dark
              :color="item.LomStatusColor"
              class="font-weight-bold"
            >
              {{ item.LomStatusTxt }}
            </v-chip>
          </template>
          <template v-slot:[`item.CurrentYearCount`]="{ item }">
            <v-chip
              small
              dark
              :color="item.CurrentYearCount > 0 ? 'green' : 'grey'"
            >
              {{ item.CurrentYearCount }}
            </v-chip>
          </template>
          <template v-slot:[`item.PreviousYearCount`]="{ item }">
            <v-chip
              small
              dark
              :color="item.PreviousYearCount > 0 ? 'green' : 'grey'"
            >
              {{ item.PreviousYearCount }}
            </v-chip>
          </template>

          <template v-slot:[`item.OldYearCount`]="{ item }">
            <v-chip
              small
              dark
              :color="item.OldYearCount > 0 ? 'green' : 'grey'"
            >
              {{ item.OldYearCount }}
            </v-chip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-overlay :value="EmailLoadingFlag">
              <v-progress-circular
                indeterminate
                size="64"
                color="white"
              ></v-progress-circular>
            </v-overlay>

            <v-tooltip bottom v-if="item.MailFlag">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#1a3a5c"
                  @click="send_bulk_email(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="20">email</v-icon>
                </v-btn>
              </template>
              <span>Send Mail</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="orange"
                  @click="addConfirmAlert(item, 3)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="20">mdi-pause-circle</v-icon>
                </v-btn>
              </template>
              <span>Suspended</span>
            </v-tooltip>

            <!-- Disaffiliated Button -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="red"
                  @click="addConfirmAlert(item, 4)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="20">mdi-cancel</v-icon>
                </v-btn>
              </template>
              <span>Disaffiliated</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import StudentScholarshipPreview from "@/view/pages/erp/student-scholarship/StudentScholarshipPreview.vue";
import StudentScholarshipApproval from "@/view/pages/erp/student-scholarship/StudentScholarshipApproval.vue";
import Swal from "sweetalert2";

export default {
  components: {
    StudentScholarshipPreview,
    StudentScholarshipApproval,
  },

  mixins: [common],
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      ProgressLoadingFlag: false,
      SearchFlag: false,
      SearchCompleted: false,
      loading: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      rows: [],
      tableColumns1: [],
      tableOptions1: {},
      selected: [],
      search: "",

      Status: null,
      StatusOptions: [
        { text: "--Select Status--", value: null },
        { text: "Suspended", value: 1 },
        { text: "Disaffiled", value: 2 },
      ],
      StatusOptionsRules: [(v) => v != null || "Status is required"],
      StatusOptionsLoading: false,

      MembershipId: null,
      member: {},

      DownloadFlag: "",
      ExcelFileName: "student_scholarship_details.xls",

      recordToEdit: "",
      record: {},
      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      editPageDescription: "Assign LO President for JCI India",

      LomDataPageTitle: "",
      LomDataPreviewFlag: false,
      LomDataParams: {},

      ZoneCode: null,
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      LomCode: null,
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      EmailLoadingFlag: false,

      selectedData: {},

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  mounted() {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      // this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
      this.LoadingFlag = false;
      this.ZoneCodeOptionsLoading = false;
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LomCodeOptionsLoading = false;
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.rows = [];
      this.selected = [];
      this.member = {};
      this.MembershipId = null;
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "suspendation_lo/disafflication_lo",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getJciYearCodeOptions();
      this.getLomCodeOptions();
      this.getZoneCodeOptions();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/lom/create";
      var params = {};
      this.pageRedirect(params, url);
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.searchForm();
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },

    searchForm() {
      console.log("searchForm is called");

      this.loading = true;

      var validate1 = this.$refs.form2.validate();
      var SearchId = this.SearchId;
      var UserInterface = SearchId == 1 ? 1 : 3;
      console.log(
        "validate1=" +
          validate1 +
          ", SearchId=" +
          SearchId +
          ", UserInterface=" +
          UserInterface
      );

      if (validate1) {
        this.SearchFlag = true;
        this.SearchCompleted = false;
        this.ProgressLoadingFlag = true;
        this.rows = [];
        this.zone = {};
        this.lom = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          server_url + "api/reports/suspended-disafflication-lo-lists";
        var upload = {
          UserInterface: 1,
          LoStatus: this.Status,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var table = {};
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SearchFlag = false;
            thisIns.ProgressLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            table = response.data.TableContent;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            thisIns.tableColumns1 = table.TableHeader;
            thisIns.rows = table.TableData;
            thisIns.SearchCompleted = true;

            if (flag == 1) {
              thisIns.toast("success", output);
              thisIns.tableOptions1 = response.data.TableOptions;
              let tableOptions1 = thisIns.tableOptions1;
              thisIns.ExcelFields = tableOptions1.ExcelFields;
              thisIns.ExcelFileName = tableOptions1.ExcelFileName;
              thisIns.DownloadFlag = tableOptions1.ExcelDownloadFlag;
            } else {
              thisIns.toast("error", output);
            }
            thisIns.loading = false;
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
            thisIns.SearchCompleted = true;
            thisIns.loading = false;
            thisIns.ProgressLoadingFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },

    send_bulk_email(item) {
      console.log("send_bulk_email is called");

      console.log("item=", item);

      if (item) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/reports/send-bulk-email-v2";
        var upload = {
          UserInterface: 1,
          LomId: item.LomId,
          LoStatus: this.Status,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.EmailLoadingFlag = true;

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()

            thisIns.EmailLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              // thisIns.toast("success", output);
              thisIns.selected = [];
            } else {
              // thisIns.toast("error", output);
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
            thisIns.EmailLoadingFlag = false;
          });
      } else {
        var message = "Select atleast one record to send email. ";
        // this.toast("error", message);
        this.sweetAlert("error", output, false);
      }
    },

    addConfirmAlert(item, status) {
      Swal.fire({
        title: "Are you sure you want to update the status?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: `Yes`,
        confirmButtonColor: "#3085d6",
        cancelButtonText: `No`,
        cancelButtonColor: "#d33",
      }).then((result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) {
          this.updateStatus(item, status);
        } else {
          this.sweetAlert("error", "You cancelled the process", true);
        }
      });
    },

    updateStatus(item, status) {
      console.log("updateActiveStatus is called");

      var validate1 = this.$refs.form2.validate();
      var tr = item;
      var LomId = tr.LomId;
      var ZoneCode = tr.ZoneId;

      var ActiveStatus = status;
      console.log(
        "validate1=" +
          validate1 +
          "LomId=" +
          LomId +
          "ActiveStatus=" +
          ActiveStatus +
          "ZoneCode=" +
          ZoneCode
      );

      if (validate1 && LomId != "" && ActiveStatus != "") {
        this.ProgressLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/lom/status-update";
        var upload = {
          UserInterface: 1,
          Year: this.JciYearCode,
          Zone: ZoneCode,
          Lom: LomId,
          Status: status,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.ProgressLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              // thisIns.searchForm();
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.ProgressLoadingFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
$navy: #1a3a5c;
$aqua: #0097d7;
$seafoam: #56bda3;

#judge-assignment-list {
  padding: 16px;

  // ── Header ────────────────────────────────────────────────────────
  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    border: none;
    box-shadow: 0 16px 48px rgba(26, 58, 92, 0.4),
      0 0 32px rgba(0, 151, 215, 0.2);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.07) 50%,
        transparent 70%
      );
      pointer-events: none;
    }
  }

  .header-gradient {
    position: relative;
    z-index: 1;
  }

  .header-backdrop-effect {
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 70%
    );
    pointer-events: none;
  }

  .header-content {
    position: relative;
    z-index: 2;
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.22) 0%,
      rgba(255, 255, 255, 0.1) 100%
    );
    backdrop-filter: blur(12px);
    border-radius: 14px;
    padding: 13px;
    border: 2px solid rgba(255, 255, 255, 0.22);
    flex-shrink: 0;
  }

  .header-title {
    font-size: clamp(1.1rem, 3vw, 1.8rem);
    font-weight: 800;
    letter-spacing: 0.4px;
    text-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);
  }

  .header-subtitle {
    font-size: clamp(0.76rem, 1.5vw, 0.9rem);
    opacity: 0.88;
    font-weight: 500;
    margin-top: 3px;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.28) !important;
    color: white !important;
    backdrop-filter: blur(10px);
  }

  // ── Filter Card ───────────────────────────────────────────────────
  .filter-card {
    border-radius: 12px !important;
    overflow: hidden;
    border: 1px solid #e0e7ef !important;
  }

  .filter-card-header {
    background: linear-gradient(90deg, $navy 0%, lighten($navy, 10%) 100%);
    padding: 10px 18px;
    display: flex;
    align-items: center;
  }

  .filter-card-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.3px;
  }

  .field-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #546e7a;
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 2px;
  }

  .required-tag {
    color: #e53935;
    font-size: 0.78rem;
    font-weight: 700;
    margin-left: 3px;
  }

  .optional-tag {
    font-size: 0.68rem;
    color: #90a4ae;
    font-weight: 500;
    margin-left: 4px;
    background: #f0f4f8;
    border-radius: 4px;
    padding: 1px 5px;
  }

  .search-btn {
    background: linear-gradient(90deg, $navy 0%, $aqua 100%) !important;
    color: #fff !important;
    font-weight: 700 !important;
    border-radius: 8px !important;
    letter-spacing: 0.4px;
    text-transform: none !important;
    height: 40px !important;
  }

  // ── Table Card ────────────────────────────────────────────────────
  .table-card {
    border-radius: 12px !important;
    border: 1px solid #e0e7ef !important;
  }

  .table-title {
    font-size: 1rem;
    font-weight: 700;
    color: $navy;
  }

  .download-btn {
    background: linear-gradient(90deg, #0288d1, #0097d7) !important;
    color: white !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    text-transform: none !important;
  }

  .search-field .v-input__slot {
    border-radius: 8px !important;
  }

  .results-table {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e8edf2;

    thead tr {
      background: linear-gradient(
        90deg,
        lighten($navy, 48%) 0%,
        #f0f7fb 100%
      ) !important;
    }

    thead th {
      font-size: 0.775rem !important;
      font-weight: 700 !important;
      color: $navy !important;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
      border-bottom: 2px solid lighten($navy, 35%) !important;
    }

    tbody tr:hover {
      background-color: #f0f9ff !important;
    }

    tbody td {
      font-size: 0.875rem;
      color: #37474f;
    }
  }

  // ── Submit footer ─────────────────────────────────────────────────
  .submit-footer {
    padding: 14px 20px;
    background-color: #f8fafc;
    flex-wrap: wrap;
    gap: 10px;
  }

  .hint-text {
    font-size: 0.8rem;
    color: #78909c;
    display: flex;
    align-items: center;
  }

  .submit-btn {
    background: linear-gradient(
      90deg,
      $seafoam 0%,
      darken($seafoam, 8%) 100%
    ) !important;
    color: #fff !important;
    font-weight: 700 !important;
    border-radius: 8px !important;
    text-transform: none !important;
    letter-spacing: 0.4px;
    min-width: 160px;

    &.v-btn--disabled {
      opacity: 0.6 !important;
    }
  }

  // ── Portal Details Card ───────────────────────────────────────────
  .portal-details-card {
    border-radius: 10px !important;
    overflow: hidden;
    border: 1px solid #e0e7ef;

    .portal-card-header {
      background: linear-gradient(90deg, $navy 0%, lighten($navy, 8%) 100%);
      padding: 12px 16px;
      display: flex;
      align-items: center;
    }

    .portal-card-title {
      font-size: 0.95rem;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: 0.3px;
    }

    .member-info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px 12px;
    }

    .info-item {
      display: flex;
      flex-direction: column;
    }

    .info-label {
      font-size: 0.68rem;
      font-weight: 600;
      color: #607d8b;
      display: flex;
      align-items: center;
      white-space: nowrap;
    }

    .info-value {
      font-size: 0.78rem;
      font-weight: 600;
      color: #263238;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  // ── No Records ────────────────────────────────────────────────────
  .no-records-card {
    border-radius: 12px !important;
    border: 1px solid #e0e7ef !important;
  }

  .no-records-db-wrapper {
    display: flex;
    justify-content: center;
  }

  .no-records-svg {
    width: 160px;
    height: 144px;
  }

  .no-records-title {
    font-size: 1.2rem;
    font-weight: 700;
    color: $navy;
    letter-spacing: 0.3px;
  }

  .no-records-subtitle {
    font-size: 0.88rem;
    color: #78909c;
    line-height: 1.6;
  }

  // ── Mobile tweaks ─────────────────────────────────────────────────
  @media (max-width: 599px) {
    padding: 10px;

    .header-icon-box {
      padding: 10px;
    }

    .results-table thead th {
      font-size: 0.7rem !important;
    }

    .submit-btn,
    .search-btn {
      width: 100%;
      min-width: unset;
    }
  }
}
</style>